<!-- ❗Errors in the form are set on line 60 -->
<script setup>
import { ref, nextTick, inject } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useAbility } from '@casl/vue'
import { VForm } from 'vuetify/components/VForm'

// Temporarily comment out the AuthProvider import since we'll modify the template
// import AuthProvider from '@/views/pages/authentication/AuthProvider.vue'
import { useGenerateImageVariant } from '@core/composable/useGenerateImageVariant'
import authV2LoginIllustrationBorderedDark from '@images/pages/auth-v2-login-illustration-bordered-dark.png'
import authV2LoginIllustrationBorderedLight from '@images/pages/auth-v2-login-illustration-bordered-light.png'
import authV2LoginIllustrationDark from '@images/pages/auth-v2-login-illustration-dark.png'
import authV2LoginIllustrationLight from '@images/pages/auth-v2-login-illustration-light.png'
import authV2MaskDark from '@images/pages/misc-mask-dark.png'
import authV2MaskLight from '@images/pages/misc-mask-light.png'
import { VNodeRenderer } from '@layouts/components/VNodeRenderer'
import { themeConfig } from '@themeConfig'
import { parsePermission, generateUserAbilityRules } from "@/plugins/casl/utils/permissionParser"
import { getDefaultDashboard } from "@/plugins/1.router/additional-routes"
import { VTextField } from 'vuetify/components/VTextField'
import axios from "axios"
import { useI18n } from 'vue-i18n'
import { useCookie } from '@core/composable/useCookie'

// Define validation rules
const requiredValidator = value => !!value || 'This field is required'

const emailValidator = value => {
  const pattern = /^[^\s@]+@[^\s@][^\s.@]*\.[^\s@]+$/

  return pattern.test(value) || 'Must be a valid email'
}

const { t } = useI18n()
const authThemeImg = useGenerateImageVariant(authV2LoginIllustrationLight, authV2LoginIllustrationDark, authV2LoginIllustrationBorderedLight, authV2LoginIllustrationBorderedDark, true)
const authThemeMask = useGenerateImageVariant(authV2MaskLight, authV2MaskDark)

definePage({
  meta: {
    layout: 'blank',
    public: true, // Mark as public to bypass all guards
  },
})

const isPasswordVisible = ref(false)
const route = useRoute()
const router = useRouter()
const { ability } = useAbility()
const updateAbilities = inject('updateAbilities', null)

const errors = ref({
  email: undefined,
  password: undefined,
})

const refVForm = ref()

const credentials = ref({
  email: '',
  password: '',
})

const rememberMe = ref(false)

const login = async () => {
  try {
    // Create a separate axios instance without baseURL to avoid double prefixing
    const loginAxios = axios.create({
      baseURL: '', // Empty base URL to avoid double prefixing
    })

    const response = await loginAxios.post('/api/auth/login', {
      email: credentials.value.email,
      password: credentials.value.password,
    })

    const res = response.data // ✅ Ensure we extract `value` correctly

    if (!res || res.success !== true) {
      console.error('Login failed, received:', res)
      errors.value = { email: [res?.message || 'Invalid email or password'] }

      return
    }
    const { token, user, permissions } = res.data

    // ✅ Store authentication details - split between cookies and localStorage to avoid size limits
    useCookie('accessToken').value = token

    // Pick the "primary" role for the cookie. Users can hold multiple roles
    // (e.g. mobile-admin + superadmin); the cookie only carries one for size
    // reasons, so prefer superadmin when present so single-role guards still
    // see it. The full array stays in localStorage.fullUserData and is what
    // the router guard / useSuperadmin actually read.
    const allRoleNames = Array.isArray(user.roles)
      ? user.roles.map(r => (typeof r === 'string' ? r : r?.name)).filter(Boolean)
      : []
    const primaryRole = allRoleNames.includes('superadmin')
      ? 'superadmin'
      : (allRoleNames[0] || user.role || 'user')

    // Store only minimal essential data in cookies (for router guards)
    const minimalUserData = {
      id: user.id,
      email: user.email,
      role: primaryRole,
      status: user.status || 'active',
      teamId: user.team_id,
      team: user.team ? {
        id: user.team.id,
        code: user.team.code,
        name: user.team.name
      } : null
    }
    useCookie('userData').value = minimalUserData

    // Store full user data in localStorage (no size limit)
    const fullUserData = {
      ...user,
      role: primaryRole,
      avatar: user.profile?.avatar || null,
    }
    localStorage.setItem('fullUserData', JSON.stringify(fullUserData))

    // Set authorization header for all future requests
    axios.defaults.headers.common['Authorization'] = `Bearer ${token}`

    // ✅ Generate team-based CASL rules from user data and store in localStorage
    const userWithTeam = {
      ...user,
      permissions,
      roles: user.roles || [user.role].filter(Boolean)
    }

    const caslPermissions = generateUserAbilityRules(userWithTeam)
    // Store CASL rules in localStorage instead of cookies
    localStorage.setItem('userAbilityRules', JSON.stringify(caslPermissions))

    // Update abilities using the injected function or direct ability update
    if (updateAbilities) {
      updateAbilities(userWithTeam)
    } else if (ability && ability.update) {
      ability.update(caslPermissions)
    } else {
      // Fallback: reload the page to reinitialize abilities
      console.warn('No ability update method available, page will reload')
      window.location.reload()
    }

    // 🔹 Get the correct default dashboard for the user's role.
    // money users land on /invoices; admin/superadmin/manager/user
    // land on /tracking. See additional-routes.js getDefaultDashboard.
    const defaultRoute = getDefaultDashboard(primaryRole)

    // 🔥 Ensure Vue detects the authentication state update
    await nextTick(() => {
      router.replace(route.query.to ? String(route.query.to) : defaultRoute)
    })

  } catch (err) {
    console.error('Login failed:', err)

    // Better error handling with detailed messages
    if (err.response) {
      const status = err.response.status
      const errorData = err.response.data || {}

      if (status === 401) {
        // Unauthorized - Invalid credentials
        errors.value = {
          email: [errorData.message || 'Invalid email or password. Please try again.'],
        }
      } else if (status === 422) {
        // Validation error
        errors.value = errorData.errors || {
          email: ['Please check your email and password format.'],
        }
      } else {
        // Other API errors
        errors.value = {
          email: [errorData.message || `Error ${status}: Login failed. Please try again.`],
        }
      }
    } else if (err.request) {
      // Network error - no response received
      errors.value = {
        email: ['Network error. Please check your connection and try again.'],
      }
    } else {
      // Something else happened while setting up the request
      errors.value = {
        email: ['Login failed. Please try again.'],
      }
    }
  }
}


const onSubmit = () => {
  refVForm.value?.validate().then(({ valid: isValid }) => {
    if (isValid)
      login()
  })
}
</script>

<template>
  <RouterLink to="/">
    <div class="auth-logo d-flex align-center gap-x-3">
      <VNodeRenderer :nodes="themeConfig.app.logo" />
      <h1 class="auth-title">
        {{ themeConfig.app.title }}
      </h1>
    </div>
  </RouterLink>

  <VRow
    no-gutters
    class="auth-wrapper bg-surface"
  >
    <VCol
      md="8"
      class="d-none d-md-flex"
    >
      <div class="position-relative bg-background w-100 me-0">
        <div
          class="d-flex align-center justify-center w-100 h-100"
          style="padding-inline: 6.25rem;"
        >
          <VImg
            max-width="613"
            :src="authThemeImg"
            class="auth-illustration mt-16 mb-2"
          />
        </div>

        <img
          class="auth-footer-mask"
          :src="authThemeMask"
          alt="auth-footer-mask"
          height="280"
          width="100"
        >
      </div>
    </VCol>

    <VCol
      cols="12"
      md="4"
      class="auth-card-v2 d-flex align-center justify-center"
    >
      <VCard
        flat
        :max-width="500"
        class="mt-12 mt-sm-0 pa-4"
      >
        <VCardText>
          <h4 class="text-h4 mb-1">
            {{ t('auth.welcome') }} <span class="text-capitalize"> {{ themeConfig.app.title }} </span>! 👋🏻
          </h4>
          <p class="mb-0">
            {{ t('auth.sign_in_message') }}
          </p>
        </VCardText>
        <VCardText>
          <VForm
            ref="refVForm"
            @submit.prevent="onSubmit"
          >
            <VRow>
              <!-- email -->
              <VCol cols="12">
                <VTextField
                  v-model="credentials.email"
                  :label="t('auth.email')"
                  placeholder="Email"
                  type="email"
                  autofocus
                  :rules="[requiredValidator, emailValidator]"
                  :error-messages="errors.email"
                  variant="outlined"
                />
              </VCol>

              <!-- password -->
              <VCol cols="12">
                <VTextField
                  v-model="credentials.password"
                  :label="t('auth.password')"
                  placeholder="············"
                  :rules="[requiredValidator]"
                  :type="isPasswordVisible ? 'text' : 'password'"
                  autocomplete="password"
                  :error-messages="errors.password"
                  :append-inner-icon="isPasswordVisible ? 'tabler-eye-off' : 'tabler-eye'"
                  variant="outlined"
                  @click:append-inner="isPasswordVisible = !isPasswordVisible"
                />

                <div class="d-flex align-center flex-wrap justify-space-between my-6">
                  <VCheckbox
                    v-model="rememberMe"
                    :label="t('auth.remember_me')"
                  />
                </div>

                <VBtn
                  block
                  type="submit"
                >
                  {{ t('auth.login') }}
                </VBtn>

                <!-- Warehouse Android app — workers grab the APK before they
                     have an account, so the link lives on the login page. -->
                <VDivider class="my-4">
                  <span class="text-caption text-medium-emphasis">{{ t('shipment.mobile_app.divider') }}</span>
                </VDivider>
                <VBtn
                  block
                  variant="tonal"
                  color="success"
                  prepend-icon="tabler-brand-android"
                  href="/downloads/godana-warehouse.apk"
                  download
                >
                  {{ t('shipment.mobile_app.download') }}
                </VBtn>
              </VCol>
            </VRow>
          </VForm>
        </VCardText>
      </VCard>
    </VCol>
  </VRow>
</template>

<style lang="scss">
@use "@core-scss/template/pages/page-auth";
</style>
